import { useEffect, useState } from 'react'
import { ethers } from 'ethers'
import BigNumber from 'bignumber.js'
import { useWeb3React } from '@web3-react/core'
import tokens from 'config/constants/tokens'
import { getBep20Contract, getCakeContract } from 'utils/contractHelpers'
import { BIG_ZERO } from 'utils/bigNumber'
import { simpleRpcProvider } from 'utils/providers'
import useRefresh from './useRefresh'
import useLastUpdated from './useLastUpdated'

type UseTokenBalanceState = {
  balance: BigNumber
  fetchStatus: FetchStatus
}

export enum FetchStatus {
  NOT_FETCHED = 'not-fetched',
  SUCCESS = 'success',
  FAILED = 'failed',
}

const useTokenBalance = (tokenAddress: string) => {
  const { NOT_FETCHED, SUCCESS, FAILED } = FetchStatus
  const [balanceState, setBalanceState] = useState<UseTokenBalanceState>({
    balance: BIG_ZERO,
    fetchStatus: NOT_FETCHED,
  })
  const { account } = useWeb3React()
  const { fastRefresh } = useRefresh()

  useEffect(() => {
    const fetchBalance = async () => {
      if (!account || !ethers.utils.isAddress(account) || !tokenAddress || !ethers.utils.isAddress(tokenAddress)) {
        console.warn('Invalid account or tokenAddress', { account, tokenAddress })
        return
      }

      const contract = getBep20Contract(tokenAddress)
      try {
        const res = await contract.balanceOf(account)
        setBalanceState({ balance: new BigNumber(res.toString()), fetchStatus: SUCCESS })
      } catch (e) {
        console.error('Failed to fetch token balance', e)
        setBalanceState((prev) => ({
          ...prev,
          fetchStatus: FAILED,
        }))
      }
    }

    if (account && tokenAddress) {
      fetchBalance()
    }
  }, [account, tokenAddress, fastRefresh])

  return balanceState
}

export const useTotalSupply = () => {
  const { slowRefresh } = useRefresh()
  const [totalSupply, setTotalSupply] = useState<BigNumber>()

  useEffect(() => {
    async function fetchTotalSupply() {
      try {
        const cakeContract = getCakeContract()
        const supply = await cakeContract.totalSupply()
        setTotalSupply(new BigNumber(supply.toString()))
      } catch (error) {
        console.error('Failed to fetch total supply', error)
      }
    }

    fetchTotalSupply()
  }, [slowRefresh])

  return totalSupply
}

export const useBurnedBalance = (tokenAddress: string) => {
  const [balance, setBalance] = useState(BIG_ZERO)
  const { slowRefresh } = useRefresh()

  useEffect(() => {
    const fetchBalance = async () => {
      if (!tokenAddress || !ethers.utils.isAddress(tokenAddress)) {
        console.warn('Invalid tokenAddress for burned balance', tokenAddress)
        return
      }

      try {
        const contract = getBep20Contract(tokenAddress)
        const res = await contract.balanceOf('0x000000000000000000000000000000000000dEaD')
        setBalance(new BigNumber(res.toString()))
      } catch (error) {
        console.error('Failed to fetch burned balance', error)
      }
    }

    fetchBalance()
  }, [tokenAddress, slowRefresh])

  return balance
}

export const useGetBnbBalance = () => {
  const [fetchStatus, setFetchStatus] = useState(FetchStatus.NOT_FETCHED)
  const [balance, setBalance] = useState(ethers.BigNumber.from(0))
  const { account } = useWeb3React()
  const { lastUpdated, setLastUpdated } = useLastUpdated()

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        if (!account || !ethers.utils.isAddress(account)) return
        const walletBalance = await simpleRpcProvider.getBalance(account)
        setBalance(walletBalance)
        setFetchStatus(FetchStatus.SUCCESS)
      } catch {
        setFetchStatus(FetchStatus.FAILED)
      }
    }

    if (account) {
      fetchBalance()
    }
  }, [account, lastUpdated])

  return { balance, fetchStatus, refresh: setLastUpdated }
}

export const useGetCakeBalance = () => {
  const tokenAddr = tokens?.cake?.address
  const { balance, fetchStatus } = useTokenBalance(tokenAddr)

  return {
    balance: ethers.BigNumber.from(balance.toString()),
    fetchStatus,
  }
}

export const useGetETHBalance = () => {
  const [fetchStatus, setFetchStatus] = useState(FetchStatus.NOT_FETCHED)
  const [balance, setBalance] = useState(BIG_ZERO)
  const { account, library } = useWeb3React()
  const { lastUpdated, setLastUpdated } = useLastUpdated()

  useEffect(() => {
    const fetchBalance = async () => {
      const ethAddress = tokens?.eth?.address?.[97]
      if (!account || !ethers.utils.isAddress(account) || !ethAddress || !ethers.utils.isAddress(ethAddress)) return

      try {
        const currencyContract = getBep20Contract(ethAddress, library)
        const walletBalance = await currencyContract.balanceOf(account)
        setBalance(new BigNumber(walletBalance.toString()))
        setFetchStatus(FetchStatus.SUCCESS)
      } catch {
        setFetchStatus(FetchStatus.FAILED)
      }
    }

    if (account) {
      fetchBalance()
    }
  }, [account, lastUpdated, library])

  return { balance, fetchStatus, refresh: setLastUpdated }
}

export default useTokenBalance
